import { useState, useCallback, useEffect } from 'react'
import { message } from 'antd'
import events from '../api/events'
import useInterval from './useInterval'
import { useAsync } from './index'

const POLLING_INTERVAL = 2000

export default function useAlertGeneratedEvent(alertId) {
  const [event, setEvent] = useState(null)
  const [isPolling, setIsPolling] = useState(true)

  const checkEventStatus = useCallback(async () => {
    try {
      const eventData = await events.getEvent(
        alertId,
        'Alert',
        'alert_generated',
      )

      if (eventData?.status === 'completed' || eventData?.status === 'failed') {
        setIsPolling(false)
        setEvent(eventData)
        if (eventData.status === 'completed') {
          message.success('Alert successfully sent to EMR')
        }
      } else if (eventData?.status === 'pending') {
        setEvent(eventData)
      } else {
        setEvent(null)
      }
    } catch (err) {
      setIsPolling(false)
      setEvent(null)
    }
  }, [alertId])

  useInterval(checkEventStatus, isPolling ? POLLING_INTERVAL : null)

  const {
    pending: isSendToEMRPending,
    execute: sendAlertGeneratedToEMR,
  } = useAsync(async () => {
    try {
      const newEvent = await events.sendAlertToEMR(alertId)
      setEvent(newEvent)

      if (newEvent?.status === 'pending') {
        setIsPolling(true)
      }
    } catch (err) {
      message.error(
        `There was a problem sending this alert to EMR: ${err.message}`,
      )
    }
  }, false)

  // Fetch event when alertId changes
  useEffect(() => {
    if (!alertId) return

    const fetchEvent = async () => {
      try {
        const eventData = await events.getEvent(
          alertId,
          'Alert',
          'alert_generated',
        )
        setEvent(eventData)
      } catch (err) {
        setEvent(null)
      }
    }

    fetchEvent()
  }, [alertId])

  // Button state logic
  const disabled = isSendToEMRPending || event !== null
  let type = 'default'
  if (event?.status === 'failed') {
    type = 'danger'
  } else if (event?.status === 'completed') {
    type = 'dashed'
  }

  const text =
    event?.status === 'completed' || event?.status === 'failed'
      ? 'Sent to EMR'
      : 'Send to EMR'

  let icon
  if (event?.status === 'failed') {
    icon = 'exclamation-circle'
  } else if (event) {
    icon = 'check-circle'
  } else {
    icon = undefined
  }

  const loading = isSendToEMRPending || isPolling

  return {
    event,
    isPolling,
    sendAlertGeneratedToEMR,
    buttonState: {
      disabled,
      type,
      icon,
      loading,
      text,
    },
  }
}
